import React, { useContext, useState } from "react";
import "@/assets/styles/auth-shell.css";
import "./LoginV2.css";
import PropTypes from "prop-types";
import { ResetPassword } from "@features/auth/modals/ResetPassword";
import { NewRegistration } from "@features/auth/modals/NewRegistration";
import { NewTherapistRegistration } from "@features/auth/modals/NewTherapistRegistration";
import Alert from "react-bootstrap/Alert";
import { useTranslation } from "react-i18next";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PersonIcon from "@mui/icons-material/Person";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { useNavigate } from "react-router-dom";
import http from "@shared/api/http";
import { BrandingContext } from "@shared/branding/BrandingContext";

const API_URL = process.env.REACT_APP_SOPHYAPP_API_URL;

// Tenants send therapists to their own link (therapist_url, already with
// ?m=<id>); domain brands to their own app (medfit -> therapeut.medfit.ch,
// see brandingConfig.js).
function therapistRedirectUrl(baseUrl, tenant, accessToken) {
  const url = new URL(tenant?.therapist_url || baseUrl);
  url.searchParams.set("auth_token", accessToken);
  if (tenant) url.searchParams.set("m", tenant.id);
  return url.toString();
}

async function loginPatient(credentials) {
  const params = {
    patient: {
      email: credentials.username,
      password: credentials.password,
    },
  };
  const response = await http.post("/:locale/v2/patient/login", params);
  return response.data;
}

async function loginTherapist(credentials) {
  const params = {
    account: {
      email: credentials.username,
      password: credentials.password,
    },
  };
  const response = await http.post("/de/v2/account/login", params);
  return response.data;
}

export default function LoginV2({ setToken, application, imagePath }) {
  const [activeTab, setActiveTab] = useState("patient");

  // Patient login state
  const [username, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);

  // Therapist login state
  const [therapistUsername, setTherapistUsername] = useState("");
  const [therapistPassword, setTherapistPassword] = useState("");
  const [therapistShowPassword, setTherapistShowPassword] = useState(false);
  const [therapistIsLoading, setTherapistIsLoading] = useState(false);
  const [therapistLoginFailed, setTherapistLoginFailed] = useState(false);

  // Modal + alert state (patient)
  const [modalShow, setModalShow] = useState(false);
  const [modalRegShow, setModalRegShow] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registrationFailed, setRegistrationFailed] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordFailed, setPasswordFailed] = useState(false);

  // Modal + alert state (therapist)
  const [therapistRegShow, setTherapistRegShow] = useState(false);
  const [therapistRegSuccess, setTherapistRegSuccess] = useState(false);
  const [therapistRegFailed, setTherapistRegFailed] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();

  // Branding customers (domain brands like medfit, and tenants) get a bare
  // login: no therapist sign-up (it starts a sophyapp trial), no sophyapp
  // status page and footer.
  const {
    brandName = application,
    isBrandingCustomer,
    tenant,
    therapistAppUrl,
    applyAccountBranding,
  } = useContext(BrandingContext) || {};

  const handlePatientSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const token = await loginPatient({ username, password });
      if (token["access_token"]) {
        // Reset the in-progress flag so a future 401 can trigger a new
        // forced logout flow, and clear leftover expired-session markers
        // from a previous parallel-login eviction.
        window.__SOPHY_SESSION_EXPIRY_IN_PROGRESS__ = false;
        localStorage.removeItem("sessionExpired");
        localStorage.setItem("userData", JSON.stringify(token));
        applyAccountBranding?.(token);
        setToken(token["access_token"]);
      } else {
        setLoginFailed(true);
      }
    } catch {
      setLoginFailed(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTherapistSubmit = async (e) => {
    e.preventDefault();
    setTherapistIsLoading(true);
    setTherapistLoginFailed(false);
    try {
      const result = await loginTherapist({
        username: therapistUsername,
        password: therapistPassword,
      });
      if (result.access_token) {
        const accountTenant = applyAccountBranding
          ? applyAccountBranding(result)
          : tenant;
        window.location.href = therapistRedirectUrl(
          therapistAppUrl,
          accountTenant,
          result.access_token
        );
      } else {
        setTherapistLoginFailed(true);
      }
    } catch {
      setTherapistLoginFailed(true);
    } finally {
      setTherapistIsLoading(false);
    }
  };

  return (
    <div className="lv2-page">
      <div className="lv2-container">
        <div className="lv2-box">
          <img src={imagePath} alt={`${brandName} logo`} className="lv2-logo" />

          {/* Tabs – Therapeut first, Patient second (active by default) */}
          <div className="lv2-tabs">
            <button
              type="button"
              className={`lv2-tab${activeTab === "therapist" ? " active" : ""}`}
              onClick={() => setActiveTab("therapist")}
            >
              <ManageAccountsIcon className="lv2-tab-icon" />
              {t("user.therapist", { defaultValue: "Therapeut" })}
            </button>
            <button
              type="button"
              className={`lv2-tab${activeTab === "patient" ? " active" : ""}`}
              onClick={() => setActiveTab("patient")}
            >
              <PersonIcon className="lv2-tab-icon" />
              {t("user.patient", { defaultValue: "Patient" })}
            </button>
          </div>

          {/* ── Therapist login form ── */}
          {activeTab === "therapist" && (
            <div className="lv2-form-container">
              <form onSubmit={handleTherapistSubmit} noValidate>
                <div className="lv2-input-wrapper">
                  <input
                    type="email"
                    placeholder={t("user.user")}
                    className="lv2-input"
                    value={therapistUsername}
                    onChange={(e) =>
                      setTherapistUsername(e.target.value.toLowerCase())
                    }
                    autoFocus
                    required
                    autoComplete="email"
                  />
                </div>
                <div className="lv2-input-wrapper lv2-password-wrapper">
                  <input
                    type={therapistShowPassword ? "text" : "password"}
                    placeholder={t("user.password")}
                    className="lv2-input"
                    value={therapistPassword}
                    onChange={(e) => setTherapistPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="lv2-password-toggle"
                    onClick={() =>
                      setTherapistShowPassword(!therapistShowPassword)
                    }
                    tabIndex="-1"
                    aria-label={
                      therapistShowPassword ? "Hide password" : "Show password"
                    }
                  >
                    {therapistShowPassword ? (
                      <VisibilityIcon style={{ fontSize: "20px" }} />
                    ) : (
                      <VisibilityOffIcon style={{ fontSize: "20px" }} />
                    )}
                  </button>
                </div>

                <div className="lv2-forgot-wrapper">
                  <a
                    href={`${API_URL}/passwords/new`}
                    className="lv2-forgot-link"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("password.forgot")}
                  </a>
                </div>

                <div className="lv2-submit-wrapper">
                  <button
                    type="submit"
                    className="lv2-submit-btn"
                    disabled={therapistIsLoading}
                  >
                    {therapistIsLoading
                      ? t("actions.loading", { defaultValue: "Lädt..." })
                      : t("user.sign-in")}
                  </button>
                </div>

                {!isBrandingCustomer && (
                  <div className="lv2-register-wrapper">
                    <p className="lv2-register-text">{t("user.no-therapist-account")}</p>
                    <span
                      className="lv2-register-link"
                      onClick={() => setTherapistRegShow(true)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) =>
                        e.key === "Enter" && setTherapistRegShow(true)
                      }
                    >
                      {t("user.register-now")}
                    </span>
                  </div>
                )}
              </form>
            </div>
          )}

          {/* ── Patient login form ── */}
          {activeTab === "patient" && (
            <div className="lv2-form-container">
              <form onSubmit={handlePatientSubmit} noValidate>
                <div className="lv2-input-wrapper">
                  <input
                    type="email"
                    placeholder={t("user.user")}
                    className="lv2-input"
                    value={username}
                    onChange={(e) => setUserName(e.target.value.toLowerCase())}
                    autoFocus
                    required
                    autoComplete="email"
                  />
                </div>
                <div className="lv2-input-wrapper lv2-password-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder={t("user.password")}
                    className="lv2-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="lv2-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <VisibilityIcon style={{ fontSize: "20px" }} />
                    ) : (
                      <VisibilityOffIcon style={{ fontSize: "20px" }} />
                    )}
                  </button>
                </div>

                <div className="lv2-forgot-wrapper">
                  <span
                    className="lv2-forgot-link"
                    onClick={() => setModalShow(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && setModalShow(true)}
                  >
                    {t("password.forgot")}
                  </span>
                </div>

                <div className="lv2-submit-wrapper">
                  <button
                    type="submit"
                    className="lv2-submit-btn"
                    disabled={isLoading}
                  >
                    {isLoading
                      ? t("actions.loading", { defaultValue: "Lädt..." })
                      : t("user.sign-in")}
                  </button>
                </div>

                <div className="lv2-register-wrapper">
                  <p className="lv2-register-text">{t("user.no-account")}</p>
                  <span
                    className="lv2-register-link"
                    onClick={() => setModalRegShow(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      e.key === "Enter" && setModalRegShow(true)
                    }
                  >
                    {t("user.register-now")}
                  </span>
                </div>

                <div className="lv2-public-access-wrapper">
                  <span
                    className="lv2-public-access-link"
                    onClick={() => navigate("/public-access")}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      e.key === "Enter" && navigate("/public-access")
                    }
                  >
                    {t("user.public-access")}
                  </span>
                </div>
              </form>
            </div>
          )}

          {!isBrandingCustomer && (
            <div className="lv2-status-wrapper">
              <a
                href="https://sophyapp-status.statuspage.io"
                target="_blank"
                rel="noreferrer"
              >
                {t("user.status-link", {
                  defaultValue: "Probleme? Aktuelle Statusmeldungen hier.",
                })}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Footer: a tenant's own legal links (if set), else hoferdigital's */}
      {tenant && (tenant.privacy_url || tenant.imprint_url) && (
        <div className="lv2-footer">
          {tenant.privacy_url && (
            <a href={tenant.privacy_url} target="_blank" rel="noreferrer">
              {t("others.privacy")}
            </a>
          )}
          {tenant.privacy_url && tenant.imprint_url && <> &bull; </>}
          {tenant.imprint_url && (
            <a href={tenant.imprint_url} target="_blank" rel="noreferrer">
              {t("others.imprint", { defaultValue: "Impressum" })}
            </a>
          )}
        </div>
      )}
      {!isBrandingCustomer && (
        <div className="lv2-footer">
          © hoferdigital gmbh &bull;{" "}
          <a
            href="https://www.hoferdigital.at/impressum-datenschutz/"
            target="_blank"
            rel="noreferrer"
          >
            {t("others.privacy")}
          </a>{" "}
          &bull;{" "}
          <a
            href="https://www.hoferdigital.at/impressum-datenschutz/"
            target="_blank"
            rel="noreferrer"
          >
            {t("others.condition")}
          </a>{" "}
          &bull;{" "}
          <a
            href="https://hoferdigital.atlassian.net/wiki/spaces/SS/pages/2949447681/Wiki+f+r+Patienten+Athleten+und+Klienten"
            target="_blank"
            rel="noreferrer"
          >
            {t("others.support")}
          </a>
        </div>
      )}

      {/* Alerts */}
      <Alert
        variant="danger"
        className="lv2-alert"
        show={loginFailed}
        onClose={() => setLoginFailed(false)}
        dismissible
      >
        <Alert.Heading>{t("user.password-wrong")}</Alert.Heading>
        <p>{t("user.check-form")}</p>
      </Alert>
      <Alert
        variant="danger"
        className="lv2-alert"
        show={therapistLoginFailed}
        onClose={() => setTherapistLoginFailed(false)}
        dismissible
      >
        <Alert.Heading>{t("user.password-wrong")}</Alert.Heading>
        <p>{t("user.check-form")}</p>
      </Alert>
      <Alert
        variant="success"
        className="lv2-alert"
        show={registrationSuccess}
        onClose={() => setRegistrationSuccess(false)}
        dismissible
      >
        <Alert.Heading>{t("user.must-registrate")}</Alert.Heading>
        <p>{t("user.check-mail")}</p>
      </Alert>
      <Alert
        variant="danger"
        className="lv2-alert"
        show={registrationFailed}
        onClose={() => setRegistrationFailed(false)}
        dismissible
      >
        <Alert.Heading>{t("user.no-registration")}</Alert.Heading>
        <p>{t("user.already-registered")}</p>
      </Alert>
      <Alert
        variant="success"
        className="lv2-alert"
        show={passwordSuccess}
        onClose={() => setPasswordSuccess(false)}
        dismissible
      >
        <Alert.Heading>{t("user.reset-password")}</Alert.Heading>
        <p>{t("user.reset-password2")}</p>
      </Alert>
      <Alert
        variant="danger"
        className="lv2-alert"
        show={passwordFailed}
        onClose={() => setPasswordFailed(false)}
        dismissible
      >
        <Alert.Heading>{t("user.reset-password")}</Alert.Heading>
        <p>{t("user.reset-password-fail")}</p>
      </Alert>

      {/* Therapist registration alerts */}
      <Alert
        variant="success"
        className="lv2-alert"
        show={therapistRegSuccess}
        onClose={() => setTherapistRegSuccess(false)}
        dismissible
      >
        <Alert.Heading>{t("user.must-registrate")}</Alert.Heading>
        <p>{t("user.check-mail")}</p>
      </Alert>
      <Alert
        variant="danger"
        className="lv2-alert"
        show={therapistRegFailed}
        onClose={() => setTherapistRegFailed(false)}
        dismissible
      >
        <Alert.Heading>{t("user.no-registration")}</Alert.Heading>
        <p>{t("user.already-registered")}</p>
      </Alert>

      {/* Modals */}
      <ResetPassword
        show={modalShow}
        setPasswordSuccess={setPasswordSuccess}
        setPasswordFailed={setPasswordFailed}
        application={application}
        onHide={() => setModalShow(false)}
      />
      <NewRegistration
        show={modalRegShow}
        setRegistrationSuccess={setRegistrationSuccess}
        setRegistrationFailed={setRegistrationFailed}
        applicationName={application}
        onHide={() => setModalRegShow(false)}
      />
      <NewTherapistRegistration
        show={therapistRegShow}
        setRegistrationSuccess={setTherapistRegSuccess}
        setRegistrationFailed={setTherapistRegFailed}
        onHide={() => setTherapistRegShow(false)}
      />
    </div>
  );
}

LoginV2.propTypes = {
  setToken: PropTypes.func.isRequired,
  application: PropTypes.string,
  imagePath: PropTypes.string,
};
